import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil, filter } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { getApiBaseUrl } from './core/utils/vanity-url.util';

// Existing imports
import { HeaderComponent } from './shared/header/header.component';
import { LoginModalComponent } from './components/login-modal/login-modal.component';
import { TopBarMenuComponent } from './shared/topbarmenu/topbarmenu.component';
import { CreateUserComponent } from './components/admin-components/user-management-create-user/user-management-create-user.component';
import { CreatePasswordComponent } from './components/create-password-modal/create-password-modal.component';
import { SnackbarComponent } from './shared/components/snack-bar/snackbar.component';
import { ResetPasswordModalComponent } from './components/reset-password-modal/reset-password-modal.component';
import { AddMembershipComponent } from './components/subscriptions/add-membership/add-membership.component';
import { CreateProductComponent } from './components/admin-components/product-management-create-product/product-management-create-product.component';
import { ContactUsModalComponent } from './shared/components/contact-us-modal/contact-us-modal.component';
import { ScrollToTopComponent } from './shared/components/scroll-to-top/scroll-to-top.component';
import { LoadingSpinnerComponent } from './shared/components/loading-spinner/loading-spinner.component';
import { NavigationInterceptorService } from './core/services/navigation-interceptor.service';
import { NavigationHistoryService } from './core/services/navigation-history.service';

// Auth service, Mode service and roles
import { AuthService } from './core/services/auth.service';
import { ModeService } from './core/services/mode.service';
import { normalizeRole, ERole } from './core/auth/roles';
import { GuideTourModalComponent } from './components/guide-tour-modal/guide-tour-modal.component';
import { GuideTourService } from './core/services/guide-tour.service';
import { SchoolSignupFlowLifecycleService } from './core/services/school-signup-flow-lifecycle.service';
import { logError } from './core/logging/logger.service';
import { ImpersonationBannerComponent } from './shared/components/impersonation-banner/impersonation-banner.component';
import { ImpersonationBroadcastService } from './core/services/impersonation-broadcast.service';
import { environment } from '../environments/environment';

/**
 * URL segments that hide the top navigation bar for BOTH brands.
 * - /register — sign-up pages have their own minimal layout
 * - /choose-memberships — plan selection has its own layout
 * - /onboarding — post-registration category/course onboarding flow
 */
const SHARED_HIDE_TOPBAR_SEGMENTS = ['/register', '/choose-memberships', '/onboarding'];

/**
 * URL segments hidden for TLL only. SK12's own checkout keeps the nav bar — this list started as a
 * TLL-only "suppress nav during checkout" request (TTT-1270) that had landed in the shared list by
 * mistake, hiding SK12's nav bar at checkout too.
 * - /checkout — TLL's checkout flow (course/bundle/subscription + success): a cleaner, distraction-free
 *   payment experience (matches every `/checkout-*` route and school-signup/checkout).
 * - /auth/landing — TLL B2C round-trip landing: shown only long enough to hydrate the session and hand
 *   off to the next step (Payment / onboarding); the header must not flash in between.
 * - /subscription-success — lands here straight off Stripe's redirect; an existing-user purchase shows
 *   its own success modal, but a registration-flow purchase immediately continues into onboarding, so
 *   the header must not flash while that decision is made.
 * - /get-started — LearnWorlds migration page: a standalone card, like the hosted B2C pages.
 */
const TLL_ONLY_HIDE_TOPBAR_SEGMENTS = ['/checkout', '/auth/landing', '/subscription-success', '/get-started'];

const HIDE_TOPBAR_SEGMENTS =
  environment.brand === 'tll'
    ? [...SHARED_HIDE_TOPBAR_SEGMENTS, ...TLL_ONLY_HIDE_TOPBAR_SEGMENTS]
    : SHARED_HIDE_TOPBAR_SEGMENTS;

/**
 * Root Application Component
 *
 * Integrates the Guide Tour system for Group Admin users in admin mode.
 * The guide tour modal is rendered at the app level to ensure it works across all routes.
 * The guide tour button is rendered only in UserManagementComponent.
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    HeaderComponent,
    TopBarMenuComponent,
    LoginModalComponent,
    ResetPasswordModalComponent,
    CreateUserComponent,
    CreateProductComponent,
    CreatePasswordComponent,
    SnackbarComponent,
    AddMembershipComponent,
    ContactUsModalComponent,
    ScrollToTopComponent,
    LoadingSpinnerComponent,
    // Guide Tour modal (global - can be triggered from anywhere)
    GuideTourModalComponent,
    ImpersonationBannerComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  // changeDetection: ChangeDetectionStrategy.OnPush // TODO: uncomment and fix state (refactoring)
})
export class AppComponent implements OnInit, OnDestroy {
  title = `${environment.portalName} Growth Portal`;
  // Seed synchronously from the current URL so the top bar is never painted for a frame before the
  // first NavigationEnd fires — e.g. a direct load / redirect onto a /checkout page must show no nav at all.
  hideTopBar =
    typeof window !== 'undefined' &&
    HIDE_TOPBAR_SEGMENTS.some((segment) => window.location.pathname.includes(segment));

  private readonly authService = inject(AuthService);
  private readonly modeService = inject(ModeService);
  private readonly guideTourService = inject(GuideTourService);
  private readonly http = inject(HttpClient);
  private readonly destroy$ = new Subject<void>();
  private readonly _schoolSignupFlowLifecycle = inject(SchoolSignupFlowLifecycleService);
  private readonly impersonationBroadcast = inject(ImpersonationBroadcastService);

  constructor(
    private router: Router,
    private navigationInterceptor: NavigationInterceptorService,
    _navHistory: NavigationHistoryService,
  ) {
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((event) => {
      const url: string = event.urlAfterRedirects || event.url;
      this.hideTopBar = HIDE_TOPBAR_SEGMENTS.some((segment) => url.includes(segment));

      // Update admin mode on each navigation
      this.updateAdminMode();
    });
  }

  ngOnInit(): void {
    this.impersonationBroadcast.changed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => window.location.reload());

    this.initializeGuideTour();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initializes the Guide Tour based on user role and mode.
   * Sets up the GuideTourService state for GroupAdmin/SubGroupAdmin detection.
   * The button visibility is controlled by the service and rendered in UserManagementComponent.
   */
  private initializeGuideTour(): void {
    this.authService.userProfile$
      .pipe(
        takeUntil(this.destroy$),
        filter((profile) => !!profile),
      )
      .subscribe({
        next: (profile) => {
          const role = normalizeRole(profile?.role ?? null);
          const isGroupAdmin = role === ERole.GroupAdmin;
          // Sub-Group Admins get the same first-time walkthrough as the org-level Group Admin who set
          // it up — previously this only checked GroupAdmin, so a Sub-Group Admin never saw it even
          // though the BE now returns showAdminGuideTour for them too (inherited from the parent org).
          const canSeeAdminGuideTour = isGroupAdmin || role === ERole.SubGroupAdmin;

          if (canSeeAdminGuideTour) {
            this.modeService.set('admin');
          }

          this.guideTourService.setGroupAdmin(canSeeAdminGuideTour);
          this.updateAdminMode();

          if (canSeeAdminGuideTour && profile?.showAdminGuideTour) {
            this.guideTourService.resetState();
            this.guideTourService.open(1);
            this.http
              .post(`${getApiBaseUrl()}/me/admin-guide-seen`, {}, { withCredentials: true })
              .subscribe({ error: (err) => logError('app.component', '[AppComponent] Failed to mark admin guide tour seen:', err) });
          }
        },
        error: (err) => {
          logError('app.component', '[AppComponent] Failed to determine user role for guide tour:',err);
        },
      });

    this.updateAdminMode();
  }

   /**
   * Updates the admin mode state in GuideTourService.
   * Called on navigation and profile changes.
   */
  private updateAdminMode(): void {
    const isAdminMode = this.modeService.isAdmin();
    this.guideTourService.setAdminMode(isAdminMode);
  }
}